// Iter 71 — Vendor Master · Detail view (Phase 1, refactored in Iter 72 for tabs).
// Tabbed shell: Overview · Services · Documents. Master Agreement dates
// renamed in Iter 72; per-service operational dates live on the Services tab.
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    PencilSimple, Trash, ArrowLeft, Check, X, FileArrowUp,
    DownloadSimple, Clock, Globe,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
    CATEGORY_LABEL, VENDOR_TYPE_LABEL, MSME_LABEL, PAYMENT_METHOD_LABEL,
    TDS_LABEL, DOC_TYPE_LABEL, humanBytes,
} from "./vendor_constants";
import VendorServicesTab from "./VendorServicesTab";
import VendorQuotationsTab from "./VendorQuotationsTab";
import VendorPOsTab from "./VendorPOsTab";
import VendorBillsTab from "./VendorBillsTab";
import VendorPaymentsTab from "./VendorPaymentsTab";

export default function VendorDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { can } = useAuth();
    const [vendor, setVendor] = useState(null);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusBusy, setStatusBusy] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const reload = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get(`/vendors/${id}`),
            api.get(`/vendors/${id}/audit`, { params: { limit: 25 } }),
        ])
            .then(([vRes, aRes]) => {
                if (cancelled) return;
                setVendor(vRes.data);
                setAudit(aRes.data.items || []);
            })
            .catch((err) => {
                if (!cancelled) {
                    toast.error(formatApiError(err, "Failed to load vendor"));
                    navigate("/vendors");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [id, navigate, reloadKey]);

    const toggleStatus = async () => {
        if (!vendor || statusBusy) return;
        const nextActive = !vendor.active;
        const action = nextActive ? "activate" : "deactivate";
        if (!window.confirm(`Are you sure you want to ${action} '${vendor.name}'?`)) return;
        setStatusBusy(true);
        try {
            await api.patch(`/vendors/${id}/status`, { active: nextActive });
            toast.success(`Vendor ${action}d`);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Status change failed"));
        } finally {
            setStatusBusy(false);
        }
    };

    const removeDoc = async (doc) => {
        if (!window.confirm(`Delete '${doc.file_name}'?`)) return;
        try {
            await api.delete(`/vendors/${id}/documents/${doc.id}`);
            toast.success("Document deleted");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    if (loading) return <div className="px-8 py-12 text-slate-500" data-testid="vendor-detail-loading">Loading…</div>;
    if (!vendor) return null;

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1300px]" data-testid="vendor-detail-page">
            <PageHeader
                eyebrow={`${vendor.code} · ${CATEGORY_LABEL[vendor.category] || vendor.category}`}
                title={vendor.name}
                description={
                    // PageHeader wraps `description` in a <p>, so the
                    // content here must be inline. Using <span> with
                    // inline-flex utility avoids the hydration warning
                    // that a <div> child would trigger.
                    <span className="inline-flex items-center gap-3 flex-wrap mt-1">
                        {vendor.active ? (
                            <Badge data-testid="vendor-active-badge" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1">
                                <Check size={10} weight="bold" /> Active
                            </Badge>
                        ) : (
                            <Badge data-testid="vendor-inactive-badge" variant="secondary" className="bg-slate-100 text-slate-500 hover:bg-slate-100 gap-1">
                                <X size={10} weight="bold" /> Inactive
                            </Badge>
                        )}
                        <span className="text-xs text-slate-500">
                            {VENDOR_TYPE_LABEL[vendor.vendor_type] || vendor.vendor_type}
                            {vendor.currency && ` · ${vendor.currency}`}
                        </span>
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2">
                        <Button data-testid="vendor-back-btn" variant="outline" onClick={() => navigate("/vendors")} className="gap-1">
                            <ArrowLeft size={14} /> Back
                        </Button>
                        {can("vendor:write") && (
                            <>
                                <Button data-testid="vendor-toggle-status-btn" variant="outline" onClick={toggleStatus} disabled={statusBusy}>
                                    {vendor.active ? "Mark Inactive" : "Activate"}
                                </Button>
                                <Button data-testid="vendor-edit-btn" onClick={() => navigate(`/vendors/${id}/edit`)} className="gap-1">
                                    <PencilSimple size={14} /> Edit
                                </Button>
                            </>
                        )}
                    </div>
                }
            />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main column (8/12) — tabbed */}
                <div className="lg:col-span-2">
                    <VendorTabs vendor={vendor} can={can} removeDoc={removeDoc} onUpload={() => setUploadOpen(true)} />
                </div>

                {/* Sidebar rail (4/12) — record metadata + audit trail */}
                <aside className="lg:col-span-1 space-y-4">
                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vendor-meta-card">
                        <h3 className="font-medium text-slate-900 mb-3">Record metadata</h3>
                        <div className="space-y-2.5 text-xs">
                            <MetaRow label="Internal ID" value={vendor.id} mono />
                            <MetaRow label="Display code" value={vendor.code} mono />
                            <MetaRow label="Created" value={formatDate(vendor.created_at)} />
                            <MetaRow label="Created by" value={vendor.created_by_email} />
                            <MetaRow label="Updated" value={formatDate(vendor.updated_at)} />
                            <MetaRow label="Updated by" value={vendor.updated_by_email} />
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vendor-audit-card">
                        <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                            <Clock size={14} className="text-slate-500" /> Activity
                        </h3>
                        {audit.length === 0 ? (
                            <div className="text-xs text-slate-400 py-2">No audit entries yet</div>
                        ) : (
                            <div className="space-y-2.5 max-h-[600px] overflow-y-auto pr-1">
                                {audit.map((a) => (
                                    <div key={a.id} className="text-xs border-l-2 border-slate-200 pl-2.5" data-testid={`vendor-audit-${a.id}`}>
                                        <div className="font-medium text-slate-900 capitalize">{a.action.replace(/_/g, " ")}</div>
                                        <div className="text-slate-500 mt-0.5">{a.actor_email}</div>
                                        <div className="text-slate-400">{formatDate(a.created_at)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </aside>
            </div>

            <DocumentUploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                vendorId={id}
                onUploaded={reload}
            />
        </div>
    );
}

// Tabbed body: Overview · Services · Documents. URL-state-backed (?tab=…)
// so refreshes / shared links land on the same tab.
function VendorTabs({ vendor, can, removeDoc, onUpload }) {
    const [params, setParams] = useSearchParams();
    const activeTab = params.get("tab") || "overview";
    const switchTab = (t) => {
        const next = new URLSearchParams(params);
        if (t === "overview") next.delete("tab"); else next.set("tab", t);
        setParams(next, { replace: true });
    };
    return (
        <Tabs value={activeTab} onValueChange={switchTab} data-testid="vendor-tabs">
            <TabsList className="mb-4">
                <TabsTrigger value="overview" data-testid="vendor-tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="services" data-testid="vendor-tab-services">Services</TabsTrigger>
                <TabsTrigger value="quotations" data-testid="vendor-tab-quotations">Quotations</TabsTrigger>
                <TabsTrigger value="pos" data-testid="vendor-tab-pos">POs</TabsTrigger>
                <TabsTrigger value="bills" data-testid="vendor-tab-bills">Bills</TabsTrigger>
                <TabsTrigger value="payments" data-testid="vendor-tab-payments">Payments</TabsTrigger>
                <TabsTrigger value="documents" data-testid="vendor-tab-documents">Documents</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-5" data-testid="vendor-tab-overview-content">
                <Section title="Contact Information">
                    <Pair label="Contact Person" value={vendor.contact_person} />
                    <Pair label="Designation" value={vendor.designation} />
                    <Pair label="Email" value={vendor.email} />
                    <Pair label="Mobile" value={vendor.mobile} mono />
                    <Pair label="Alternate Contact" value={vendor.alternate_contact} mono />
                </Section>

                <Section title="Tax & Compliance">
                    <Pair label="PAN" value={vendor.pan} mono />
                    <Pair label="GSTIN" value={vendor.gstin} mono />
                    <Pair label="MSME Status" value={MSME_LABEL[vendor.msme_status] || vendor.msme_status} />
                    <Pair label="TDS Category" value={TDS_LABEL[vendor.tds_category] || vendor.tds_category} />
                </Section>

                <Section title="Address">
                    <Pair label="Address Line 1" value={vendor.address_line1} wide />
                    <Pair label="Address Line 2" value={vendor.address_line2} wide />
                    <Pair label="City" value={vendor.city} />
                    <Pair label="State" value={vendor.state} />
                    <Pair label="Country" value={vendor.country} />
                    <Pair label="Pincode" value={vendor.pincode} mono />
                </Section>

                <Section title="Commercial & Master Agreement">
                    <Pair label="Payment Terms" value={vendor.payment_terms} />
                    <Pair label="Credit Period" value={vendor.credit_period_days ? `${vendor.credit_period_days} days` : "—"} />
                    <Pair label="Currency" value={vendor.currency} />
                    <Pair label="Preferred Payment" value={PAYMENT_METHOD_LABEL[vendor.preferred_payment_method] || vendor.preferred_payment_method} />
                    <Pair label="Master Agreement Start" value={vendor.master_agreement_start_date ? formatDate(vendor.master_agreement_start_date) : "—"} />
                    <Pair label="Master Agreement End" value={vendor.master_agreement_end_date ? formatDate(vendor.master_agreement_end_date) : "—"} />
                </Section>

                <Section title="Bank Details">
                    <Pair label="Bank Name" value={vendor.bank_name} />
                    <Pair label="Account Number" value={vendor.account_number} mono />
                    <Pair label="IFSC" value={vendor.ifsc} mono />
                    <Pair label="Beneficiary Name" value={vendor.beneficiary_name} />
                </Section>

                <Section title="Additional">
                    <Pair label="Website" wide value={
                        vendor.website ? (
                            <a className="text-indigo-600 hover:underline inline-flex items-center gap-1" href={vendor.website} target="_blank" rel="noreferrer">
                                <Globe size={12} /> {vendor.website}
                            </a>
                        ) : "—"
                    } />
                    <Pair label="Notes" wide value={vendor.notes || "—"} />
                </Section>
            </TabsContent>

            <TabsContent value="services" data-testid="vendor-tab-services-content">
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <VendorServicesTab vendor={vendor} />
                </div>
            </TabsContent>

            <TabsContent value="quotations" data-testid="vendor-tab-quotations-content">
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <VendorQuotationsTab vendor={vendor} />
                </div>
            </TabsContent>

            <TabsContent value="pos" data-testid="vendor-tab-pos-content">
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <VendorPOsTab vendor={vendor} />
                </div>
            </TabsContent>

            <TabsContent value="bills" data-testid="vendor-tab-bills-content">
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <VendorBillsTab vendor={vendor} />
                </div>
            </TabsContent>

            <TabsContent value="payments" data-testid="vendor-tab-payments-content">
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <VendorPaymentsTab vendor={vendor} />
                </div>
            </TabsContent>

            <TabsContent value="documents" data-testid="vendor-tab-documents-content">
                <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vendor-documents-section">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-medium text-slate-900">Documents</h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                PDF / DOC / DOCX / PNG / JPG up to 10 MB. Max 20 attachments per vendor.
                            </p>
                        </div>
                        {can("vendor:write") && (
                            <Button data-testid="vendor-upload-btn" size="sm" onClick={onUpload} className="gap-1">
                                <FileArrowUp size={14} /> Upload
                            </Button>
                        )}
                    </div>
                    {(vendor.documents || []).length === 0 ? (
                        <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md">
                            No documents attached
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {vendor.documents.map((d) => (
                                <div key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`vendor-doc-${d.id}`}>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-slate-900 truncate">{d.file_name}</div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100 mr-1">
                                                {DOC_TYPE_LABEL[d.document_type] || d.document_type}
                                            </Badge>
                                            {humanBytes(d.size_bytes)} · uploaded {formatDate(d.uploaded_at)}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <a
                                            data-testid={`vendor-doc-download-${d.id}`}
                                            href={d.file_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                                        >
                                            <DownloadSimple size={12} /> Download
                                        </a>
                                        {can("vendor:write") && (
                                            <Button
                                                data-testid={`vendor-doc-delete-${d.id}`}
                                                size="sm" variant="ghost" onClick={() => removeDoc(d)}
                                                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 h-7 w-7 p-0"
                                            >
                                                <Trash size={14} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </TabsContent>
        </Tabs>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-medium text-slate-900 mb-4">{title}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                {children}
            </div>
        </div>
    );
}

function Pair({ label, value, mono, wide }) {
    return (
        <div className={wide ? "sm:col-span-2" : ""}>
            <div className="reedos-eyebrow text-[10px]">{label}</div>
            <div className={`text-sm text-slate-900 mt-1 ${mono ? "font-mono" : ""} break-words`}>
                {value || <span className="text-slate-300">—</span>}
            </div>
        </div>
    );
}

function MetaRow({ label, value, mono }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="text-slate-500 shrink-0">{label}</div>
            <div className={`text-slate-900 text-right break-all ${mono ? "font-mono" : ""}`}>
                {value || "—"}
            </div>
        </div>
    );
}

function DocumentUploadDialog({ open, onClose, vendorId, onUploaded }) {
    const fileRef = useRef(null);
    const [docType, setDocType] = useState("other");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) { toast.error("Please choose a file"); return; }
        if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB limit"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("document_type", docType);
            await api.post(`/vendors/${vendorId}/documents`, fd, {
                headers: { "Content-Type": undefined },  // axios derives boundary
            });
            toast.success("Document uploaded");
            onUploaded();
            onClose();
            setDocType("other");
            if (fileRef.current) fileRef.current.value = "";
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="vendor-upload-dialog">
                <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                    <DialogDescription>
                        Select a file (PDF / DOC / DOCX / PNG / JPG, max 10 MB) and tag the document type.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs text-slate-600 mb-1.5 inline-block">Document Type</Label>
                        <select
                            data-testid="vendor-upload-doctype"
                            value={docType}
                            onChange={(e) => setDocType(e.target.value)}
                            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm"
                        >
                            {Object.entries(DOC_TYPE_LABEL).map(([k, l]) => (
                                <option key={k} value={k}>{l}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs text-slate-600 mb-1.5 inline-block">File</Label>
                        <input
                            data-testid="vendor-upload-file"
                            ref={fileRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                            className="block w-full text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-slate-300 file:bg-white file:text-slate-700 file:cursor-pointer"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="vendor-upload-submit" onClick={submit} disabled={busy} className="gap-1">
                        <FileArrowUp size={14} /> {busy ? "Uploading…" : "Upload"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
